import { P2GBox, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useEffect, useMemo, useState } from 'react';
import {
  BookingDetails,
  ParcelTrackingRecord,
  TrackingResult,
  TrackingSummaryApiModel
} from '~/lib/api/smartsend/types';
import Loading from '../../../../../components/loading/Loading';

import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { P2GAlert } from '@parcel2go/core-ui';
import { PanelButtons } from './PanelButtons';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GStepper } from '@parcel2go/core-ui';
import { P2GTable } from '@parcel2go/core-ui';
import { P2GTableHead } from '@parcel2go/core-ui';
import { P2GTableCell } from '@parcel2go/core-ui';
import { P2GTableRow } from '@parcel2go/core-ui';
import { P2GTableBody } from '@parcel2go/core-ui';
import { P2GDate } from '@parcel2go/core-ui';
import { P2GCdnImage } from '@parcel2go/core-ui';
import { SettingsCache } from '~/lib/consts/settings-cache';
import LinkButton from '../../LinkButton';
import { P2GTooltip } from '@parcel2go/core-ui';
import ConvertToTrackingSummary from './ConvertToTrackingSummary';
import { P2GImageMessage } from '~/components/imagemessage';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import useTrackingLink from '~/hooks/use-tracking-link';

interface props {
  close?: () => void;
  getTracking: () => Promise<TrackingResult>;
  bookingDetails: BookingDetails;
  trackingDetails: TrackingSummaryApiModel;
  trackingNumber?: string;
  senderIsoCode: string;
  recipientIsoCode: string;
}

type TrackinStatus = 'Dropped Off / Collected' | 'InTransit' | 'At Depot' | 'Out for Delivery' | 'Delivered';

export const ItemOrderTrackingPanel: React.FC<props> = (props) => {
  const isXs = useP2GMediaQueryOnly('xs');
  const { generateTrackingPageUrl } = useTrackingLink();
  const [trackingDetails, setSummary] = useState({ ...props.trackingDetails });
  const [tracking, setTracking] = useState<ParcelTrackingRecord[]>(null);
  const [trackable, setTrackable] = useState(true);
  const [rts, setRts] = useState(false);
  const [rtsLink, setRtsLink] = useState('');
  const [localCourierLink, setLocalCourierLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const latestStatus: TrackinStatus = useMemo(() => {
    return getCompletedSteps(trackingDetails)[0];
  }, [trackingDetails]);
  const rstWeeks = props.recipientIsoCode != props.senderIsoCode ? 6 : 2;
  const options: TrackinStatus[] = [
    'Dropped Off / Collected',
    'InTransit',
    'At Depot',
    'Out for Delivery',
    'Delivered'
  ];

  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  // Map tracking status to proper translation keys
  const getTranslationKey = (status: TrackinStatus): string => {
    switch (status) {
      case 'Dropped Off / Collected':
        return 'DROPPED_OFF_COLLECTED';
      case 'InTransit':
        return 'IN_TRANSIT';
      case 'At Depot':
        return 'AT_DEPOT';
      case 'Out for Delivery':
        return 'OUT_FOR_DELIVERY';
      case 'Delivered':
        return 'DELIVERED';
      default:
        return 'UNKNOWN_STATUS';
    }
  };

  // Get translated step labels for the stepper
  const getTranslatedSteps = (): string[] => {
    return options.map((status) => t(`TRACKING_STEPPER_STEP_${getTranslationKey(status)}`, { defaultValue: status }));
  };

  // Get translated completed steps for the stepper
  const getTranslatedCompletedSteps = (trackingDetails: TrackingSummaryApiModel): string[] => {
    const completedStatuses = getCompletedSteps(trackingDetails);
    return completedStatuses.map((status) =>
      t(`TRACKING_STEPPER_STEP_${getTranslationKey(status)}`, { defaultValue: status })
    );
  };

  useEffect(() => {
    if (props.bookingDetails) {
      props
        .getTracking()
        .then((r) => {
          if (r) {
            const summary = ConvertToTrackingSummary(r, trackingDetails);
            setTrackable(summary.trackable);
            setRts(summary.rts);
            setRtsLink(summary.links?.RTS);
            setLocalCourierLink(summary.links?.LocalCourierTrackingLink);
            setSummary(summary.summary);
            setTracking(r.Tracking);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  function getCompletedSteps(t: TrackingSummaryApiModel): TrackinStatus[] {
    const output = [];

    if (!t) {
      return output;
    }

    if (t.Collected || t.DroppedOff) {
      output.unshift('Dropped Off / Collected');
    }
    if (t.InTransit) {
      output.unshift('InTransit');
    }
    if (t.AtDepot) {
      output.unshift('At Depot');
    }
    if (t.DeliveryScheduled) {
      output.unshift('Out for Delivery');
    }
    if (t.Delivered) {
      output.unshift('Delivered');
    }
    return output;
  }

  function getTrackingImage() {
    if (rts) {
      return '/theme/tracking/notifications/tracking-error.png';
    }

    if (localCourierLink) {
      return '/theme/tracking/notifications/local-tracking.png?v=1';
    }

    switch (latestStatus) {
      case 'Delivered':
        return '/theme/tracking/notifications/tracking-delivered.png';

      case 'InTransit':
      case 'Out for Delivery':
        return '/theme/tracking/notifications/tracking-intransit.png';

      case 'Dropped Off / Collected':
      case 'At Depot':
        return '/theme/tracking/notifications/tracking-default.png';

      default:
        return '/theme/tracking/notifications/no-tracking.png';
    }
  }

  const localButton = () => {
    if (localCourierLink) {
      return (
        <P2GTooltip
          title={
            (
              <>
                <P2GText>{t('LOCAL_TRACKING_TOOLTIP_TITLE', { defaultValue: 'View Local Tracking' })}</P2GText>
                <P2GText variant="body2" component="p">
                  {t('LOCAL_BUTTON_TOOLTIP_SUBTITLE', {
                    defaultValue:
                      "Click this button to redirect to the courier's local delivery tracking page for the latest customs updates"
                  })}
                </P2GText>
              </>
            ) as any
          }
          children={
            <LinkButton variant="button" target="_blank" href={localCourierLink} id="button-local-tracking">
              {t('VIEW_LOCAL_TRACKING_BUTTON', { defaultValue: 'View Local Tracking' })}
            </LinkButton>
          }
        />
      );
    }
    return null;
  };

  function renderInfoBox() {
    if (rts) {
      return (
        <>
          <P2GText component="p">
            {t('RETURN_PARCEL_INFO', { defaultValue: 'This parcel is being returned to the sender.' })}
          </P2GText>
          <P2GAlert color="error">
            {rtsLink ? (
              <>
                <P2GText>
                  <Trans
                    i18nKey="RETURN_PARCEL_INFO_LINK"
                    t={t}
                    components={{ a: <P2GLink href={rtsLink} target="_blank" /> }}
                    value={{ returnTime: rstWeeks }}
                    defaults="Depending on the courier, the return of this parcel may take up to {{returnTime}} weeks. Please <a>raise an enquiry</a> after this time if it hasn't been received."
                  />
                </P2GText>
              </>
            ) : (
              <>
                <P2GText>
                  <Trans
                    i18nKey="RETURN_PARCEL_INFO_NO_LINK"
                    t={t}
                    value={{ returnTime: rstWeeks }}
                    defaults="Depending on the courier, the return of this parcel may take up to {{returnTime}} weeks. Please raise an
                  enquiry after this time if it hasn't been received."
                  />
                </P2GText>
              </>
            )}
          </P2GAlert>
        </>
      );
    }

    switch (latestStatus) {
      case 'InTransit':
        return (
          <>
            <P2GText component="p">
              {t(`ORDER_TRACKING_PANEL_${getTranslationKey(latestStatus)}`, { defaultValue: latestStatus })}
            </P2GText>
            <P2GAlert color="info">
              <P2GText>{t('PARCEL_IN_TRANSIT_INFO', { defaultValue: 'Your parcel is intransit' })}</P2GText>
            </P2GAlert>
          </>
        );
      case 'At Depot':
        return (
          <>
            <P2GText component="p">
              {t(`ORDER_TRACKING_PANEL_${getTranslationKey(latestStatus)}`, { defaultValue: latestStatus })}
            </P2GText>
            <P2GAlert color="info">
              <P2GText>
                {t('PARCEL_READY_FOR_DELIVERY_INFO', {
                  defaultValue: 'Your parcel is at the depot being prepared for delivery.'
                })}
              </P2GText>
            </P2GAlert>
          </>
        );
      case 'Out for Delivery':
        return (
          <>
            <P2GText component="p">
              {t(`ORDER_TRACKING_PANEL_${getTranslationKey(latestStatus)}`, { defaultValue: latestStatus })}
            </P2GText>
            <P2GAlert color="info">
              <P2GText>
                {t('PARCEL_OUT_FOR_DELIVERY_INFO', {
                  defaultValue: 'Your parcel is with the courier and is out for delivery.'
                })}
              </P2GText>
            </P2GAlert>
          </>
        );
      case 'Delivered':
        return (
          <>
            <P2GText component="p">
              {t(`ORDER_TRACKING_PANEL_${getTranslationKey(latestStatus)}`, { defaultValue: latestStatus })}
            </P2GText>
            <P2GAlert color="success">
              <P2GText>{t('PARCEL_DELIVERED_INFO', { defaultValue: 'Your parcel has been delivered.' })}</P2GText>
            </P2GAlert>
          </>
        );
      case 'Dropped Off / Collected':
        return (
          <>
            <P2GText component="p">
              {t(`ORDER_TRACKING_PANEL_${getTranslationKey(latestStatus)}`, { defaultValue: latestStatus })}
            </P2GText>
            <P2GAlert color="info">
              <P2GText>
                {t('PARCEL_DROPPED_OFF_INFO', {
                  defaultValue: 'Your parcel has been dropped off, or collected by the courier.'
                })}
              </P2GText>
            </P2GAlert>
          </>
        );
      default:
        return (
          <P2GAlert color="info">
            <P2GText>
              {t('AWAITING_ORDER_COMFIRMATION_INFO', {
                defaultValue:
                  'We are waiting for confirmation that the courier has your parcel. If you already dropped off your parcel, please allow up to 24 hours for the tracking to update.'
              })}
            </P2GText>
          </P2GAlert>
        );
    }
  }

  if (loading) {
    return <Loading />;
  }

  function renderTracking() {
    if (!props.bookingDetails) {
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/no-tracking.png"
          message={t('NO_TRACKING_FOUND_FALLBACK', { defaultValue: 'No Tracking Found' })}
          contentKey="image-smartsend-orderpanel-no-tracking-found"
        >
          <P2GAlert color="info">
            <P2GText>
              {t('NO_TRACKING_FOUND_ALERT', {
                defaultValue:
                  "We don't have any booking details for this order. It might have been booked using an external courier."
              })}
            </P2GText>
          </P2GAlert>
        </P2GImageMessage>
      );
    }

    return (
      <P2GBox>
        <P2GGrid container alignItems="center" wrap="nowrap" alignContent="center">
          <P2GGrid item xs={12} sm={6}>
            <P2GStepper
              activeStep={options.indexOf(latestStatus) + 1}
              steps={getTranslatedSteps()}
              orientation="vertical"
              completedSteps={getTranslatedCompletedSteps(trackingDetails)}
            />
          </P2GGrid>
          {SettingsCache.settings.Features.includes('Tracking') && (
            <P2GGrid item xs={12} sm={6}>
              <P2GBox textAlign="center">
                {SettingsCache.settings.Features.includes('Illustrations') && (
                  <P2GBox mt={-4}>
                    <P2GCdnImage style={{ width: '90%' }} src={getTrackingImage()} alt={''} />
                  </P2GBox>
                )}
                {localCourierLink ? (
                  localButton()
                ) : (
                  <LinkButton
                    variant="button"
                    disabled={!props.bookingDetails?.TrackingUrl}
                    target="_blank"
                    href={generateTrackingPageUrl(props.bookingDetails.Reference)}
                  >
                    {t('VIEW_TRACKING_PAGE_BUTTON', { defaultValue: isXs ? 'Tracking' : 'Tracking Page' })}
                  </LinkButton>
                )}
              </P2GBox>
            </P2GGrid>
          )}
        </P2GGrid>
        <P2GBox mt={2}>
          {showDetails ? (
            <P2GBox>
              {tracking.map((t, i) => (
                <P2GBox key={i} mt={i > 0 ? 2 : 0}>
                  <P2GTable>
                    <P2GTableHead>
                      <P2GTableCell>{t.TrackingNumber}</P2GTableCell>
                      <P2GTableCell></P2GTableCell>
                    </P2GTableHead>
                    <P2GTableBody>
                      {t.TrackingEvents.map((te) => (
                        <P2GTableRow key={te.Description}>
                          <P2GTableCell>
                            <P2GDate variant="body2" date={new Date(te.Timestamp)} />
                          </P2GTableCell>
                          <P2GTableCell>{te.Description}</P2GTableCell>
                        </P2GTableRow>
                      ))}
                    </P2GTableBody>
                  </P2GTable>
                </P2GBox>
              ))}
              {localCourierLink && localButton()}
            </P2GBox>
          ) : (
            <P2GBox textAlign="center">
              <P2GLink onClick={() => setShowDetails(true)}>
                {t('SHOW_TRACKING_HISTORY_LINK', { defaultValue: 'Show tracking history' })}
              </P2GLink>
            </P2GBox>
          )}
        </P2GBox>
      </P2GBox>
    );
  }

  return (
    <P2GBox textAlign="left">
      <SidePanelSections>
        {trackable ? (
          <>
            <SidePanelSection title={t('ORDER_TRACKING_SUBTITLE', { defaultValue: 'Order Tracking' })}>
              {renderTracking()}
            </SidePanelSection>
            {trackingDetails && (
              <SidePanelSection
                title={t('CURRENT_TRACKING_INFO_SUBTITLE', { defaultValue: 'Current Tracking Information' })}
              >
                {renderInfoBox()}
              </SidePanelSection>
            )}
          </>
        ) : (
          <SidePanelSection
            title={t('UNTRACKED_SERVICE_SUBTITLE', { defaultValue: 'This is an untracked service.' })}
          />
        )}
      </SidePanelSections>
      <P2GBox mt={2} />
      <PanelButtons onCancel={props.close} />
    </P2GBox>
  );
};
